/* __next_internal_client_entry_do_not_use__ WhenVisibilityIsImminent auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { getDocument } from "@faire/web--source/common/globals/getDocument";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { useResponsiveProp } from "@faire/web--source/ui/hooks/useResponsiveProp";
import isChromatic from "chromatic/isChromatic";
import React, { startTransition, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
/**
 * This component allows content to be deferred from rendering when it is currently off the
 * visibile viewport by some threshold. Very useful to speed up rendering on a page's primary
 * content. E.g. on a page with 100 product-tiles (each tile having fairly deep component tree),
 * we get meaningful rendering savings by wrapping each tile in this component.
 *
 * Since this component is only intended to improve page load performance, once content has been
 * rendered, it will not be unmounted.
 *
 * <p>Child components of this can optionally be lazily loaded, which allows for the javascript
 * to also be deferred!
 *
 * <p>Example usage:
 * <pre>
 *
 *    <WhenVisibilityIsImminent customizePlaceholder={this.renderFallBack()}>
 *      <React.Suspense fallback={this.renderFallBack()}>
 *        <LazyExpensiveComponent />
 *      </React.Suspense>
 *   </WhenVisibilityIsImminent>
 *
 * </pre>
 */ export var WhenVisibilityIsImminent = function(props) {
    /*
     * TL;DR: we want to force render in test env.
     *
     * When testing a component that uses <WhenVisibilityIsImminent> in its render method, we want to
     * force this component to render. If we don't make this change here, then in each test we have to
     * make some changes to force this component to render.
     *
     * The only exception for a test class where we don't force rendering is "WhenVisibilityIsImminent.test.tsx"
     * since we want to test its functionality properly.
     * */ var testEnv = "production" === "test" && !props.shouldRunNormallyInTest;
    var ssr = useIsSSR();
    var _ref;
    var shouldForceRender = (_ref = testEnv || getGlobalProperty("isPrerender", false) || ssr && props.enableSsr || isChromatic() || props.forceRender) !== null && _ref !== void 0 ? _ref : false;
    return /*#__PURE__*/ _jsx(IntersectionObserverImplementation, _object_spread_props(_object_spread({}, props), {
        shouldForceRender: shouldForceRender
    }));
};
var isInIframe = function() {
    try {
        var _getWindow, _getWindow1;
        return !Object.is((_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.self, (_getWindow1 = getWindow()) === null || _getWindow1 === void 0 ? void 0 : _getWindow1.top);
    } catch (e) {
        return true;
    }
};
var supportDocumentRoot = function() {
    var document_root_supported = true;
    try {
        new IntersectionObserver(function() {}, {
            root: getWindow().document
        });
    } catch (e) {
        if (e instanceof TypeError) {
            document_root_supported = false;
        }
    }
    return document_root_supported;
};
var IntersectionObserverImplementation = function(param) {
    var shouldForceRender = param.shouldForceRender, removeWhenInvisible = param.removeWhenInvisible, threshold = param.threshold, tmp = param.topThreshold, responsiveTopThreshold = tmp === void 0 ? threshold !== null && threshold !== void 0 ? threshold : -100 : tmp, tmp1 = param.bottomThreshold, responsiveBottomThreshold = tmp1 === void 0 ? threshold !== null && threshold !== void 0 ? threshold : -100 : tmp1, customizePlaceholder = param.customizePlaceholder, children = param.children, useTransition = param.useTransition;
    var topThreshold = useResponsiveProp(responsiveTopThreshold);
    var bottomThreshold = useResponsiveProp(responsiveBottomThreshold);
    var _useState = _sliced_to_array(useState({
        visible: shouldForceRender,
        placeholderHeight: undefined
    }), 2), state = _useState[0], setState = _useState[1];
    useEffect(function() {
        setState(function(state) {
            return {
                visible: shouldForceRender || state.visible,
                placeholderHeight: undefined
            };
        });
    }, [
        shouldForceRender
    ]);
    var handleVisible = function() {
        setState(function(state) {
            if (state.visible) {
                return state;
            }
            return {
                visible: true,
                placeholderHeight: undefined
            };
        });
    };
    var handleInvisible = function() {
        setState(function(state) {
            var _childRef_current_firstElementChild, _childRef_current;
            if (!state.visible) {
                return state;
            }
            var height = undefined;
            var clientHeight = (_childRef_current = childRef.current) === null || _childRef_current === void 0 ? void 0 : (_childRef_current_firstElementChild = _childRef_current.firstElementChild) === null || _childRef_current_firstElementChild === void 0 ? void 0 : _childRef_current_firstElementChild.clientHeight;
            if (clientHeight && isFinite(clientHeight)) {
                height = Math.round(clientHeight);
            }
            return {
                visible: false,
                placeholderHeight: height !== null && height !== void 0 ? height : state.placeholderHeight
            };
        });
    };
    var intersectionCallBack = useCallback(function(entries) {
        if (entries.some(function(entry) {
            return entry.isIntersecting;
        })) {
            if (useTransition) {
                startTransition(function() {
                    handleVisible();
                });
            } else {
                handleVisible();
            }
            // disconnect the observer once the element is visible when removeWhenInvisible is false
            if (!removeWhenInvisible) {
                var _intersectionObserver_current;
                (_intersectionObserver_current = intersectionObserver.current) === null || _intersectionObserver_current === void 0 ? void 0 : _intersectionObserver_current.disconnect();
            }
        } else if (removeWhenInvisible) {
            handleInvisible();
        }
    }, [
        removeWhenInvisible,
        useTransition
    ]);
    var initializeIntersectionObserver = useCallback(function() {
        var inIframe = isInIframe();
        var root = null;
        if (inIframe) {
            if (supportDocumentRoot()) {
                root = getDocument();
            } else {
                var _getDocument;
                root = (_getDocument = getDocument()) === null || _getDocument === void 0 ? void 0 : _getDocument.scrollingElement;
            }
        }
        return new IntersectionObserver(intersectionCallBack, {
            // When we run e2e in cypress, the whole website is inside an iframe,
            // and that means by default the intersection observer will watch for
            // the cypress window's viewport, so in this case we need to specify
            // a root element. The best solution here is to pass document directly
            // as document.scrollingElement.getBoundingClientRect gives the full
            // size of page content instead of just the viewport (which means
            // targets will always intersect with the root and trigger the handleVisible
            // method). Since not all browsers (ie. Safari 13.1.2) support document
            // root, we need to do a check here.
            root: root,
            rootMargin: "".concat(-topThreshold, "px 0px ").concat(-bottomThreshold, "px 0px")
        });
    }, [
        bottomThreshold,
        intersectionCallBack,
        topThreshold
    ]);
    var intersectionObserver = useRef(undefined);
    var updateIntersectionObserver = useCallback(function(node) {
        if (node && elementToObserveRef.current !== node.firstElementChild) {
            if (elementToObserveRef.current) {
                var _intersectionObserver_current;
                (_intersectionObserver_current = intersectionObserver.current) === null || _intersectionObserver_current === void 0 ? void 0 : _intersectionObserver_current.unobserve(elementToObserveRef.current);
            }
            elementToObserveRef.current = node.firstElementChild;
            if (elementToObserveRef.current) {
                var _intersectionObserver_current1;
                (_intersectionObserver_current1 = intersectionObserver.current) === null || _intersectionObserver_current1 === void 0 ? void 0 : _intersectionObserver_current1.observe(elementToObserveRef.current);
            }
        }
    }, []);
    var childRef = useRef(undefined);
    var elementToObserveRef = useRef(null);
    var mutationObserverRef = useRef(undefined);
    var refCallback = useCallback(function(node) {
        var // Disconnect the previous observers
        _intersectionObserver_current, _mutationObserverRef_current;
        if (!node) {
            return;
        }
        (_intersectionObserver_current = intersectionObserver.current) === null || _intersectionObserver_current === void 0 ? void 0 : _intersectionObserver_current.disconnect();
        (_mutationObserverRef_current = mutationObserverRef.current) === null || _mutationObserverRef_current === void 0 ? void 0 : _mutationObserverRef_current.disconnect();
        // Initialize the intersection observer when the node is mounted
        intersectionObserver.current = initializeIntersectionObserver();
        // Set any previous observerd element to null
        elementToObserveRef.current = null;
        childRef.current = node;
        // We can't just watch `node`, because IntersectionObserver doesn't work with `display: contents`.
        // So, we watch the first child of `node` instead. We handle cases where the element can change.
        mutationObserverRef.current = new MutationObserver(function() {
            updateIntersectionObserver(node);
        });
        mutationObserverRef.current.observe(node, {
            childList: true
        });
        updateIntersectionObserver(node);
    }, [
        updateIntersectionObserver,
        initializeIntersectionObserver
    ]);
    useEffect(function() {
        return function() {
            var _intersectionObserver_current, _mutationObserverRef_current;
            (_intersectionObserver_current = intersectionObserver.current) === null || _intersectionObserver_current === void 0 ? void 0 : _intersectionObserver_current.disconnect();
            (_mutationObserverRef_current = mutationObserverRef.current) === null || _mutationObserverRef_current === void 0 ? void 0 : _mutationObserverRef_current.disconnect();
        };
    }, []);
    var shouldDisplayPlaceHolder = !state.visible;
    if (!shouldForceRender && (shouldDisplayPlaceHolder || removeWhenInvisible)) {
        return /*#__PURE__*/ _jsx(Contents, {
            ref: refCallback,
            children: shouldDisplayPlaceHolder ? /*#__PURE__*/ _jsx(Placeholder, {
                customizePlaceholder: customizePlaceholder,
                height: state.placeholderHeight
            }) : /*#__PURE__*/ _jsx(_Fragment, {
                children: children
            })
        });
    } else {
        return children;
    }
};
var Placeholder = function(param) {
    var customizePlaceholder = param.customizePlaceholder, height = param.height;
    if (typeof customizePlaceholder === "function") {
        return customizePlaceholder(height);
    } else if (customizePlaceholder) {
        return customizePlaceholder;
    } else {
        return /*#__PURE__*/ _jsx("div", {
            style: {
                height: "10px"
            }
        });
    }
};
var Contents = styled.div.withConfig({
    componentId: "sc-ff0622a6-0"
})([
    "display:contents;"
]);
